/**
 * External references — ids an entity carries in OTHER systems.
 *
 * The generic answer to "where do I put the vendor id Epicor gave us": a map of
 * system name → id, e.g. `{ EPICOR: 'V00123', SAP: '4711' }`. Nothing here is
 * specific to one ERP; the system name is free text.
 *
 * System names are normalised to upper case so "Epicor" and "EPICOR" cannot
 * become two entries for the same system. An update replaces the whole map.
 */
import { z } from 'zod';
export const EXTERNAL_REFERENCE_MAX_ENTRIES = 20;
/** Systems offered as suggestions in editors; any other name is accepted. */
export const SUGGESTED_EXTERNAL_SYSTEMS = ['EPICOR', 'SAP', 'ORACLE', 'NETSUITE', 'DYNAMICS', 'TALLY'];
const systemNameSchema = z
    .string()
    .trim()
    .min(1, 'System name is required')
    .max(40, 'System name must be at most 40 characters')
    .regex(/^[A-Za-z0-9 _.-]+$/, 'System name may only contain letters, digits, space, _ . -')
    .transform((s) => s.toUpperCase());
const externalIdSchema = z
    .string()
    .trim()
    .min(1, 'Id is required')
    .max(100, 'Id must be at most 100 characters');
export const externalReferencesSchema = z
    .record(z.string(), z.string())
    .superRefine((value, ctx) => {
    if (Object.keys(value).length > EXTERNAL_REFERENCE_MAX_ENTRIES) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `At most ${EXTERNAL_REFERENCE_MAX_ENTRIES} external references`,
        });
    }
})
    .transform((value, ctx) => {
    const out = {};
    for (const [rawKey, rawId] of Object.entries(value)) {
        const key = systemNameSchema.safeParse(rawKey);
        const id = externalIdSchema.safeParse(rawId);
        if (!key.success || !id.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [rawKey],
                message: (key.success ? id : key).error?.issues[0]?.message ?? 'Invalid external reference',
            });
            continue;
        }
        if (key.data in out) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [rawKey],
                message: `Duplicate system "${key.data}"`,
            });
            continue;
        }
        out[key.data] = id.data;
    }
    return out;
});
/** "EPICOR · V00123" for the first entry, or undefined when there are none. */
export const formatPrimaryExternalReference = (refs) => {
    const first = refs ? Object.entries(refs)[0] : undefined;
    return first ? `${first[0]} · ${first[1]}` : undefined;
};
